// API service for backend communication
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class ApiService {
  constructor() {
    this.API_BASE_URL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const token = await this.getAuthToken();
    
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    const url = `${this.API_BASE_URL}${endpoint}`;
    
    // Log API URL in development to help debug
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', url);
    }

    try {
      const response = await fetch(url, config);
      
      // Handle non-JSON responses
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        return { error: `Server returned non-JSON response: ${response.status}` };
      }

      if (!response.ok) {
        return { error: data.error || data.message || 'Request failed' };
      }

      return { data };
    } catch (error) {
      console.error('API Request Error:', {
        url,
        error: error.message,
        apiBaseUrl: this.API_BASE_URL,
        envApiUrl: process.env.REACT_APP_API_URL
      });
      return { error: error instanceof Error ? error.message : 'Network error' };
    }
  }

  async getAuthToken() {
    // Get Firebase auth token
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken();
    }
    return null;
  }


  // Admin functions
  async setUserRole(userId, role) {
    return this.request('/admin/set-role', {
      method: 'POST',
      body: JSON.stringify({ userId, role }),
    });
  }

  async approveHost(hostId) {
    return this.request('/admin/approve-host', {
      method: 'POST',
      body: JSON.stringify({ hostId }),
    });
  }

  async adminPublishTrip(tripId) {
    return this.request('/admin/publish-trip', {
      method: 'POST',
      body: JSON.stringify({ tripId }),
    });
  }

  /** Impersonation login — POST /api/adminLogin (secret-protected). */
  async adminLogin({ uid, secret }) {
    return this.request('/adminLogin', {
      method: 'POST',
      body: JSON.stringify({ uid, secret }),
    });
  }

  // User/Auth methods
  async getUserData(uid) {
    return this.request(`/auth/user/${uid}`, {
      method: 'GET',
    });
  }

  async createUser({ displayName, email, phone } = {}) {
    return this.request('/auth/user', {
      method: 'POST',
      body: JSON.stringify({
        displayName: displayName || '',
        email: email || '',
        phone: phone || '',
      }),
    });
  }

  /** Fire-and-forget client report when phone signup fails (no auth required). */
  async logPhoneSignupFailure(payload) {
    return this.request('/auth/phone-signup-failure', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateUser(uid, updates) {
    return this.request(`/auth/user/${uid}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Storage methods
  async uploadImage(file, path) {
    const formData = new FormData();
    formData.append('file', file);
    if (path) {
      formData.append('path', path);
    }

    const token = await this.getAuthToken();
    const response = await fetch(`${this.API_BASE_URL}/storage/upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      return { error: data.error || 'Upload failed' };
    }
    return { data };
  }

  async uploadImages(files, basePath) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    if (basePath) {
      formData.append('basePath', basePath);
    }

    const token = await this.getAuthToken();
    const response = await fetch(`${this.API_BASE_URL}/storage/upload-multiple`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      return { error: data.error || 'Upload failed' };
    }
    return { data: { urls: data.urls } };
  }

  // Booking methods
  async createBooking(bookingData) {
    return this.request('/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async getBooking(bookingId) {
    return this.request(`/bookings/${bookingId}`, {
      method: 'GET',
    });
  }

  async getClientBookings(clientId) {
    return this.request(`/bookings/client/${clientId}`, {
      method: 'GET',
    });
  }

  async getHostBookings(hostId) {
    return this.request(`/bookings/host/${hostId}`, {
      method: 'GET',
    });
  }

  async getHostInquiries(hostId) {
    return this.request(`/bookings/host/${hostId}/inquiries`, {
      method: 'GET',
    });
  }

  async getHostBookingsOnly(hostId) {
    return this.request(`/bookings/host/${hostId}/bookings-only`, {
      method: 'GET',
    });
  }

  async getClientInquiries(clientId) {
    return this.request(`/bookings/client/${clientId}/inquiries`, {
      method: 'GET',
    });
  }

  async getTripBookings(tripId) {
    return this.request(`/bookings/trip/${tripId}`, {
      method: 'GET',
    });
  }

  async updateBookingStatus(bookingId, status) {
    return this.request(`/bookings/${bookingId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async cancelBooking(bookingId) {
    return this.request(`/bookings/${bookingId}/cancel`, {
      method: 'POST',
    });
  }

  async createPaymentIntent(bookingId, amount) {
    return this.request(`/bookings/${bookingId}/payment-intent`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  async confirmPayment(paymentIntentId) {
    return this.request('/bookings/payment/confirm', {
      method: 'POST',
      body: JSON.stringify({ paymentIntentId }),
    });
  }

  // Trip methods
  async getPublishedTrips() {
    return this.request('/trips/published', {
      method: 'GET',
    });
  }

  async getTrip(tripId) {
    return this.request(`/trips/${tripId}`, {
      method: 'GET',
    });
  }

  async getHostTrips(hostId) {
    return this.request(`/trips/host/${hostId}`, {
      method: 'GET',
    });
  }

  async createTrip(tripData) {
    return this.request('/trips', {
      method: 'POST',
      body: JSON.stringify(tripData),
    });
  }

  async updateTrip(tripId, updates) {
    return this.request(`/trips/${tripId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async publishTrip(tripId) {
    return this.request(`/trips/${tripId}/publish`, {
      method: 'POST',
    });
  }

  async unpublishTrip(tripId) {
    return this.request(`/trips/${tripId}/unpublish`, {
      method: 'POST',
    });
  }

  async deleteTrip(tripId) {
    return this.request(`/trips/${tripId}`, {
      method: 'DELETE',
    });
  }

  // Host methods
  async getHostProfile(userId) {
    return this.request(`/hosts/${userId}`, {
      method: 'GET',
    });
  }

  async createHostProfile(userId, bio) {
    return this.request('/hosts/create', {
      method: 'POST',
      body: JSON.stringify({ userId, bio }),
    });
  }

  async updateHostProfile(userId, updates) {
    return this.request(`/hosts/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async updateHostVerification(hostId, verified) {
    return this.request('/hosts/verify', {
      method: 'POST',
      body: JSON.stringify({ hostId, verified }),
    });
  }

  // Message methods
  async getMessageThread(threadId) {
    return this.request(`/messages/thread/${threadId}`, {
      method: 'GET',
    });
  }

  async getThreadMessages(threadId) {
    return this.request(`/messages/thread/${threadId}/messages`, {
      method: 'GET',
    });
  }

  async sendMessage(threadId, messageData) {
    return this.request(`/messages/thread/${threadId}/messages`, {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  }

  async getUserThreads(userId) {
    return this.request(`/messages/user/${userId}/threads`, {
      method: 'GET',
    });
  }

  async getOrCreateBookingThread(bookingId, participants) {
    return this.request(`/messages/booking/${bookingId}/thread`, {
      method: 'POST',
      body: JSON.stringify({ participants }),
    });
  }

  // Localization (public) — NG / GH / KE focused markets; defaults to US.
  // Pass browser timezone so Accra / Lagos / Nairobi detect when IP geo fails.
  async getLocalization(country = null, options = {}) {
    const params = new URLSearchParams();
    if (country) params.set('country', country);
    const timezone =
      options.timezone ||
      (typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : null);
    if (timezone) params.set('timezone', timezone);
    const query = params.toString();
    return this.request(`/localization${query ? `?${query}` : ''}`, {
      method: 'GET',
      headers: {
        ...(timezone ? { 'X-Timezone': timezone } : {}),
        ...options.headers,
      },
    });
  }

  // Workspace methods
  async getWorkspace() {
    return this.request('/workspace/me', {
      method: 'GET',
    });
  }

  async updateBusiness(updates) {
    return this.request('/workspace/business', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async checkBookingAlias(alias, locationId = null) {
    const params = new URLSearchParams({ alias: alias || '' });
    if (locationId) params.set('locationId', locationId);
    return this.request(`/workspace/booking-alias/check?${params.toString()}`, {
      method: 'GET',
    });
  }

  async updateBookingAlias(alias, locationId = null) {
    return this.request('/workspace/booking-alias', {
      method: 'PUT',
      body: JSON.stringify({ alias, locationId }),
    });
  }

  // Locations
  async getWorkspaceLocations() {
    return this.request('/workspace/locations', { method: 'GET' });
  }

  async createWorkspaceLocation(data) {
    return this.request('/workspace/locations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWorkspaceLocation(locationId, data) {
    return this.request(`/workspace/locations/${locationId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async setDefaultWorkspaceLocation(locationId) {
    return this.request(`/workspace/locations/${locationId}`, {
      method: 'PATCH',
      body: JSON.stringify({ isDefault: true }),
    });
  }

  async deleteWorkspaceLocation(locationId) {
    return this.request(`/workspace/locations/${locationId}`, {
      method: 'DELETE',
    });
  }

  async getOnboardingOptions() {
    return this.request('/workspace/onboarding/options', { method: 'GET' });
  }

  async completeOnboarding(payload) {
    return this.request('/workspace/onboarding', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getWorkspaceClients(locationId = null) {
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', locationId);
    const query = params.toString();
    return this.request(`/workspace/clients${query ? `?${query}` : ''}`, { method: 'GET' });
  }

  async createWorkspaceClient(data) {
    return this.request('/workspace/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWorkspaceClient(clientId, data) {
    return this.request(`/workspace/clients/${clientId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteWorkspaceClient(clientId) {
    return this.request(`/workspace/clients/${clientId}`, {
      method: 'DELETE',
    });
  }

  async getWorkspaceClient(clientId) {
    return this.request(`/workspace/clients/${clientId}`, { method: 'GET' });
  }

  async getWorkspaceServices(locationId = null) {
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', locationId);
    const query = params.toString();
    return this.request(`/workspace/services${query ? `?${query}` : ''}`, { method: 'GET' });
  }

  async createWorkspaceService(data) {
    return this.request('/workspace/services', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWorkspaceService(serviceId, data) {
    return this.request(`/workspace/services/${serviceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteWorkspaceService(serviceId, locationId = null) {
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', locationId);
    const query = params.toString();
    return this.request(`/workspace/services/${serviceId}${query ? `?${query}` : ''}`, {
      method: 'DELETE',
    });
  }

  async getWorkspaceServiceCategories(locationId = null) {
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', locationId);
    const query = params.toString();
    return this.request(`/workspace/service-categories${query ? `?${query}` : ''}`, {
      method: 'GET',
    });
  }

  async createWorkspaceServiceCategory(data) {
    return this.request('/workspace/service-categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWorkspaceServiceCategory(categoryId, data) {
    return this.request(`/workspace/service-categories/${categoryId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteWorkspaceServiceCategory(categoryId, locationId = null) {
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', locationId);
    const query = params.toString();
    return this.request(`/workspace/service-categories/${categoryId}${query ? `?${query}` : ''}`, {
      method: 'DELETE',
    });
  }

  async getWorkspaceStaff(locationId = null) {
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', locationId);
    const query = params.toString();
    return this.request(`/workspace/staff${query ? `?${query}` : ''}`, { method: 'GET' });
  }

  async createWorkspaceStaff(data) {
    return this.request('/workspace/staff', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWorkspaceStaff(staffId, data) {
    return this.request(`/workspace/staff/${staffId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteWorkspaceStaff(staffId) {
    return this.request(`/workspace/staff/${staffId}`, {
      method: 'DELETE',
    });
  }

  async askWorkspaceAgent({ question, locationId = null, history = [] }) {
    return this.request('/workspace/agent/ask', {
      method: 'POST',
      body: JSON.stringify({ question, locationId, history }),
    });
  }

  async getWorkspaceSales(locationId = null) {
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', locationId);
    const query = params.toString();
    return this.request(`/workspace/sales${query ? `?${query}` : ''}`, { method: 'GET' });
  }

  async retryWorkspacePayouts(
    locationId = null,
    appointmentId = null,
    amount = null,
    confirmAccountNumber = null
  ) {
    return this.request('/workspace/sales/payouts/retry', {
      method: 'POST',
      body: JSON.stringify({ locationId, appointmentId, amount, confirmAccountNumber }),
    });
  }

  async getWorkspaceAppointments({ date, from, to, locationId } = {}) {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (from) params.set('from', String(from));
    if (to) params.set('to', String(to));
    if (locationId) params.set('locationId', locationId);
    const query = params.toString();
    return this.request(`/workspace/appointments${query ? `?${query}` : ''}`, {
      method: 'GET',
    });
  }

  async getWorkspaceAvailability({ serviceId, date, staffId, locationId }) {
    const params = new URLSearchParams({ serviceId, date });
    if (staffId) params.set('staffId', staffId);
    if (locationId) params.set('locationId', locationId);
    return this.request(`/workspace/availability?${params.toString()}`, {
      method: 'GET',
    });
  }

  async createWorkspaceAppointment(data) {
    return this.request('/workspace/appointments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getWorkspaceBlocks({ date, from, to, locationId } = {}) {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (from) params.set('from', String(from));
    if (to) params.set('to', String(to));
    if (locationId) params.set('locationId', locationId);
    const query = params.toString();
    return this.request(`/workspace/blocks${query ? `?${query}` : ''}`, {
      method: 'GET',
    });
  }

  async createWorkspaceBlock(data) {
    return this.request('/workspace/blocks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteWorkspaceBlock(blockId) {
    return this.request(`/workspace/blocks/${blockId}`, {
      method: 'DELETE',
    });
  }

  async updateWorkspaceAppointment(appointmentId, data) {
    return this.request(`/workspace/appointments/${appointmentId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Public booking link (no auth)
  async getPublicBooking(slug) {
    return this.request(`/booking/${slug}`, { method: 'GET' });
  }

  async getPublicBookingMeta(slug) {
    return this.request(`/booking/${slug}/meta`, { method: 'GET' });
  }

  async getPublicAvailability(slug, { serviceId, date, staffId }) {
    const params = new URLSearchParams({ serviceId, date });
    if (staffId) params.set('staffId', staffId);
    return this.request(`/booking/${slug}/availability?${params.toString()}`, {
      method: 'GET',
    });
  }

  async createPublicBooking(slug, data) {
    return this.request(`/booking/${slug}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async checkoutPublicBooking(slug, data) {
    return this.request(`/booking/${slug}/checkout`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async verifyPublicBookingPayment(slug, { transactionId, txRef, appointmentId } = {}) {
    const params = new URLSearchParams();
    if (transactionId) params.set('transaction_id', transactionId);
    if (txRef) params.set('tx_ref', txRef);
    if (appointmentId) params.set('appointment', appointmentId);
    return this.request(`/booking/${slug}/payments/verify?${params.toString()}`, {
      method: 'GET',
    });
  }

  async getWorkspacePayout(locationId = null) {
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', locationId);
    const query = params.toString();
    return this.request(`/workspace/payout${query ? `?${query}` : ''}`, { method: 'GET' });
  }

  async getWorkspacePayoutBanks(country, locationId = null) {
    const params = new URLSearchParams({ country });
    if (locationId) params.set('locationId', locationId);
    return this.request(`/workspace/payout/banks?${params.toString()}`, { method: 'GET' });
  }

  async resolveWorkspacePayoutAccount({ accountBank, accountNumber, locationId }) {
    return this.request('/workspace/payout/resolve', {
      method: 'POST',
      body: JSON.stringify({ accountBank, accountNumber, locationId }),
    });
  }

  async saveWorkspacePayout(data) {
    return this.request('/workspace/payout', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Questionnaire methods
  async saveQuestionnaire(userId, answers) {
    return this.request('/auth/questionnaire', {
      method: 'POST',
      body: JSON.stringify({ userId, answers }),
    });
  }
}

export const api = new ApiService();

